import {
  useDeleteBankAccountMutation,
  useUpdateBankAccountMutation,
} from '@/api'
import type { BankInfo, CompanyStatus } from '@/api/models/company'
import type { Item } from '@/components'
import { useDrawer, useRole } from '@/hooks'
import { TrashIcon } from '@/modules/MedComplex/icons'
import { TableHeadCell } from '@/modules/MedComplexList/components/MedComplexTable/components'
import { SearchIcon } from '@/modules/MedComplexList/icons'
import { BankStatus } from '@/modules/Organization/components'
import { OrganizationContext } from '@/modules/Organization/Organization'
import { DrawerType } from '@/store/app'
import AddIcon from '@mui/icons-material/Add'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputAdornment,
  OutlinedInput,
  Pagination,
  PaginationItem,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useCallback, useContext, useEffect, useState } from 'react'
import { BankAccount } from './drawers'
import { styles } from './styles'

export const BankAccounts = () => {
  const { banks, getBanks, organizationId } = useContext(OrganizationContext)
  const { showDrawer } = useDrawer()

  const [updateBank] = useUpdateBankAccountMutation()
  const [deleteBank] = useDeleteBankAccountMutation()

  const [chosenItem, setChosenItem] = useState<BankInfo | null>(null)

  const onChangeMain = async (bank: BankInfo) => {
    if (bank?.companyId && bank?.id) {
      updateBank({
        companyId: bank.companyId,
        accountId: bank.id,
        payload: { is_main: !bank.is_main },
      }).then(() => {
        refetchBanks()
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filterQuery, setFilterQuery] = useState<any>()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sortQuery, setSortQuery] = useState<any>()

  const [cards, setCards] = useState<Item[]>([
    {
      id: 1,
      text: 'Номер расчетного счёта',
      data: (item: BankInfo) => item.number,
      visible: true,
      sort: (sort: 'asc' | 'desc' | '') => {
        setSortQuery({ short_title: sort })
        setSortMode(1, sort)
      },
    },
    {
      id: 2,
      text: 'Наименование банка',
      data: (item: BankInfo) => item.bank_name,
      visible: true,
      sort: (sort: 'asc' | 'desc' | '') => {
        setSortQuery({ status: sort })
        setSortMode(2, sort)
      },
    },
    {
      id: 3,
      text: 'Статус',
      data: (item: BankInfo) => (
        <BankStatus status={item.status as CompanyStatus} />
      ),
      visible: true,
      sort: (sort: 'asc' | 'desc' | '') => {
        setSortQuery({ status: sort })
        setSortMode(3, sort)
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // filter: (handleClose: any, setMode: any) => (
      //   <StatusFilter
      //     setFilterRequest={setFilterQuery}
      //     handleClose={handleClose}
      //     setMode={setMode}
      //   />
      // ),
    },
  ])

  const setSortMode = (id: number, mode: 'asc' | 'desc' | '') => {
    setCards((prev) =>
      prev.map((item: Item) =>
        item.id === id ? { ...item, sortMode: mode } : { ...item, sortMode: '' }
      )
    )
  }

  const [page, setPage] = useState<number>(1)
  const [pageQty, setPageQty] = useState(0)
  const [perPage, setPerPage] = useState<number>(50)

  useEffect(() => {
    if (page && perPage) {
      getBanks &&
        getBanks({
          id: organizationId,
          params: {
            // query: JSON.stringify({
            //   page: page,
            //   per_page: perPage,
            //   //orderBy: { id: 'desc' },
            //   orderBy: sortQuery,
            //   ...filterQuery,
            // }),
            page: page,
            per_page: perPage,
            //orderBy: { id: 'desc' },
            orderBy: sortQuery,
            ...filterQuery,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }).then((response: any) => {
          setPageQty(response?.data?.last_page)
        })
    }
  }, [page, perPage, sortQuery, filterQuery])

  const refetchBanks = () => {
    if (page && perPage) {
      getBanks({
        id: organizationId,
        params: {
          query: JSON.stringify({
            page: page,
            per_page: perPage,
            //orderBy: { id: 'desc' },
            orderBy: sortQuery,
            ...filterQuery,
          }),
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then((response: any) => {
        setPageQty(response?.data?.last_page)
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [typingTimer, setTypingTimer] = useState<any>()
  const typingListener = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    inputValue: any,
    typingPause: number,
    callback: () => void
  ) => {
    clearTimeout(typingTimer)
    setTypingTimer(setTimeout(callback, typingPause))
  }

  const searchRequest = (event: React.ChangeEvent<HTMLInputElement>) => {
    typingListener(event.target.value, 800, () => {
      if (event.target.value !== '') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setFilterQuery((prev: any) => ({
          ...prev,
          search: event.target.value,
        }))
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setFilterQuery((prev: any) => delete prev.search)
      }
    })
  }

  const [selected, setSelected] = useState<BankInfo[]>([])
  const [allSelected, setAllSelected] = useState(false)

  const allCheckboxHandle = useCallback(
    (_: React.SyntheticEvent, value: boolean) => {
      setAllSelected((prev) => !prev)
      setSelected(value ? banks?.data : [])
    },
    [banks, setSelected]
  )

  const checkboxHandle = useCallback(
    (bank: BankInfo) => {
      const newSelected = selected?.includes(bank)
        ? selected?.filter((item) => item.id !== bank.id)
        : [...selected, bank]

      setAllSelected(
        newSelected?.length > 0 && newSelected?.length === banks?.data?.length
      )
      setSelected(newSelected)
    },
    [selected, banks, setSelected]
  )

  const handleDeleteBanks = useCallback(() => {
    if (selected.length > 0) {
      for (const { id } of selected) {
        if (id) {
          deleteBank({
            companyId: organizationId,
            accountId: id,
          }).then(() => {
            refetchBanks()
          })
        }
      }
      setSelected([])
      setAllSelected(false)
    }
  }, [selected, deleteBank, organizationId, refetchBanks])

  const { access } = useRole('ORGANIZATION', ['UPDATE', 'ADMIN'])

  return (
    <Stack sx={{ backgroundColor: '#EEEEEE' }} gap={'8px'}>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        // m={'4px 12px 12px 12px'}
      >
        {/* <FormControl
          sx={{
            '& .MuiInputBase-input': {
              // WebkitBoxShadow: '0 0 0 100px white inset',
              // backgroundColor: 'white',
            },
            '& .MuiOutlinedInput-root': {
              maxWidth: '255px',
              minWidth: '255px',
              maxHeight: '36px',
              minHeight: '36px',
              paddingRight: '6px',
              backgroundColor: 'white',
              '& input::placeholder': {
                color: '#8E8E8E',
                opacity: 1,
              },
              '& fieldset': {
                border: '2px solid #D9D9D9',
              },
              '& .MuiInputAdornment-root': {
                pointerEvents: 'none',
              },
              '&.MuiOutlinedInput-root:hover': {
                opacity: 1,
                '& fieldset': {
                  border: '2px solid #000000',
                },
              },
              '&.MuiOutlinedInput-root.Mui-focused': {
                opacity: 1,
                '& fieldset': {
                  border: '2px solid #009FDA',
                },
              },
            },
          }}
        >
          <OutlinedInput
            placeholder="Поиск"
            endAdornment={
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            }
            inputProps={{ form: { autoComplete: 'off' } }}
            autoComplete="off"
            onChange={searchRequest}
          />
        </FormControl> */}
        <Stack direction={'row'} gap={'12px'}>
          {access && (
            <>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => {
                  setChosenItem(null)
                  showDrawer(DrawerType.ORGANIZATION_BANK_ACCOUNT)
                }}
              >
                Добавить счет
              </Button>
              <Button
                disabled={selected?.length === 0}
                variant="outlined"
                sx={{
                  background: 'white',
                  color: '#009FDA',
                  border: '2px solid #009FDA',
                  '&:hover': {
                    border: '2px solid #BDBDBD',
                  },
                  '&:disabled': {
                    border: '2px solid #BDBDBD',
                  },
                }}
                onClick={handleDeleteBanks}
              >
                <TrashIcon />
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <Box sx={styles.scrollBar}>
        <TableContainer sx={styles.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: '#FAFAFA' }}>
                  Действующий
                </TableCell>
                {access && (
                  <TableCell
                    sx={{ backgroundColor: '#FAFAFA', padding: '0px' }}
                  >
                    <Checkbox
                      // sx={{ width: '0px' }}
                      color="primary"
                      checked={allSelected}
                      onChange={allCheckboxHandle}
                    />
                  </TableCell>
                )}
                {cards?.map((card: Item) =>
                  card?.visible || !!card?.alwaysVisible ? (
                    <TableHeadCell item={card} key={card?.id} />
                  ) : null
                )}
                <TableCell
                  sx={{ backgroundColor: '#FAFAFA', width: '100%' }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {banks?.data?.map((item: BankInfo) => (
                <TableRow
                  key={item.id}
                  sx={{
                    ...styles.tableRow,
                    pointerEvents: access ? 'auto' : 'none',
                  }}
                  onClick={() => {
                    showDrawer(DrawerType.ORGANIZATION_BANK_ACCOUNT)
                    setChosenItem(item)
                  }}
                >
                  <TableCell
                    sx={{
                      backgroundColor: '#FAFAFA',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: access ? 'auto' : 'none',
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      onChangeMain(item)
                    }}
                  >
                    <Radio
                      disableRipple
                      checked={item.is_main}
                      onChange={() => onChangeMain(item)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  {access && (
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ padding: '0px' }}
                    >
                      <Checkbox
                        color="primary"
                        checked={selected?.includes(item)}
                        onChange={() => checkboxHandle(item)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                  )}
                  {cards?.map((card: Item) =>
                    card.visible || !!card?.alwaysVisible ? (
                      <TableCell key={card.id}>{card.data(item)}</TableCell>
                    ) : null
                  )}
                  <TableCell sx={{ width: '100%' }}></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* <Stack
          direction={'row'}
          justifyContent={'center'}
          sx={{
            backgroundColor: '#FAFAFA',
            position: 'sticky',
            bottom: '0px',
            padding: '8px',
          }}
        >
          <Pagination
            count={pageQty}
            page={page}
            onChange={(_, num) => setPage(num)}
            variant="outlined"
            shape="rounded"
            sx={{
              //marginY: 3, marginX: "auto",
              '& .Mui-selected': {
                backgroundColor: '#009FDA !important',
                color: 'white !important',
              },
              '& .Mui-selected:hover': {
                backgroundColor: 'rgb(0,159,218) !important',
                color: 'white !important',
              },
            }}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                // component={NavLink}
                //to={`/medcomplex/${item.page}`}
                // to={`/organization`}
              />
            )}
          />
        </Stack> */}
      </Box>
      <BankAccount
        isFirstBank={banks?.data?.length === 0}
        refetchBanks={refetchBanks}
        bank={chosenItem}
        setBank={setChosenItem}
      />
    </Stack>
  )
}
